import _extends from "@babel/runtime/helpers/esm/extends"
import { useThree, useFrame } from "@react-three/fiber"

import * as React from "react"
import { OrbitControls as OrbitControls$1 } from "three-stdlib"
import animateCamera from "@lib/camera/animateCamera"
import TWEEN from "@tweenjs/tween.js"
import { useStore } from "@state/store"
import useSocketStore from "@state/useSocketStore"
import { useCameraSockets } from "@lib/useCameraSockets"
import { PerspectiveCamera } from "@react-three/drei"
import { useLocation } from "react-router-dom"
import { Vector3 } from "three"

const CameraControls = () => {
  const { camRef, isIpad } = useCameraSockets("main")

  React.useEffect(() => {
    camRef.current && camRef.current.lookAt(new Vector3(0, -0.2, 0))
  }, [])

  if (isIpad) {
    return <CameraControlsTween />
  } else {
    return (
      <PerspectiveCamera
        near={0.01}
        far={10}
        fov={75}
        makeDefault
        position={[-0.6, 0.6, 0.6]}
        ref={camRef}
      />
    )
  }
}

const CameraControlsTween = () => {
  const { pathname } = useLocation()

  const cameraSettings = useSocketStore((s) => s.cameraSettings)
  const invalidate = useThree((state) => state.invalidate)
  const defaultCamera = useThree((state) => state.camera)

  const ref = React.useRef() as any

  React.useEffect(() => {
    setTimeout(() => {
      if (pathname === "/residences") {
        ref.current.minDistance = 1
        ref.current.enablePan = false
      } else {
        ref.current.enablePan = true
        ref.current.minDistance = 0.3
      }
    }, 1000)
  }, [pathname])

  const controlsCatcher = useStore((s) => s.controlsCatcher)

  const controls = React.useMemo(
    () => new OrbitControls$1(defaultCamera),
    [defaultCamera]
  )
  useFrame(() => {
    defaultCamera.updateProjectionMatrix()
    TWEEN.update()
    controls.update()
  }, -1)

  React.useEffect(() => {
    controls.connect(controlsCatcher)
    return () => void controls.dispose()
  }, [controlsCatcher, controls, invalidate])

  ///////////////////////////////////////////////////////////////////////////////////////////////

  const currentViewport = React.useMemo(() => {
    let viewport = {
      target: cameraSettings.target,
      camera: cameraSettings.position,
      fov: cameraSettings.fov,
      rotateEnabled: cameraSettings.enabled,
      delay: cameraSettings.delay,
      duration: cameraSettings.duration,
    }

    return viewport
  }, [cameraSettings])

  // console.log("CURRENT VIEWPORT", currentViewport)
  const animateToLookAt = React.useCallback(
    (destination) => {
      const {
        camera: position,
        target,
        fov,
        rotateEnabled,
        duration,
      } = destination
      if (position && target && controls && fov) {
        const [cx, cy, cz] = position
        const [tx, ty, tz] = target

        animateCamera(defaultCamera, ref.current, {
          // position: position,
          position: {
            x: cx,
            y: cy,
            z: cz,
          },
          target: {
            x: tx,
            y: ty,
            z: tz,
          },
          fov: fov,
          duration: duration,
          enabled: rotateEnabled,
        })
      }
    },
    [defaultCamera]
  )

  const [globalDelay] = React.useState(50)

  React.useEffect(() => {
    if (currentViewport) {
      let cameraSettings = {
        camera: currentViewport?.camera,
        target: currentViewport?.target,
        fov: currentViewport?.fov,
        rotateEnabled: currentViewport?.rotateEnabled,
        delay: currentViewport?.delay,
        duration: currentViewport?.duration,
      }
      setTimeout(
        () => {
          animateToLookAt(cameraSettings)
        },
        currentViewport?.delay ? currentViewport?.delay : globalDelay
      )
    }
  }, [currentViewport, animateToLookAt, globalDelay])

  ///////////////////////////////

  return /*#__PURE__*/ React.createElement(
    "primitive",
    _extends(
      {
        ref: ref,
        object: controls,
        enableDamping: true,
        maxDistance: 1.5,
        minDistance: 0.2,
        maxPolarAngle: Math.PI / 2 - 0.3,
        minPolarAngle: 0,
      }
      // restProps
    )
  )
}
export default CameraControls
